import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory2';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const ItemModal = ({ open, onClose, onSave, ItemData = null, mode = 'create', existingItems = [] }) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [formData, setFormData] = useState({
    Name: '', PurchaseRate: '', SaleRate: '', GSTpercent: '', HSNcode: '', OpeningStock: ''
  });
  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      if (ItemData && mode !== 'create') {
        setFormData({
          Id: ItemData.Id || '',
          Name: ItemData.Name || '',
          PurchaseRate: ItemData.PurchaseRate ?? '',
          SaleRate: ItemData.SaleRate ?? '',
          GSTpercent: ItemData.GSTpercent ?? '',
          HSNcode: ItemData.HSNcode || '',
          OpeningStock: ItemData.OpeningStock ?? '',
        });
      } else {
        setFormData({ Name: '', PurchaseRate: '', SaleRate: '', GSTpercent: '', HSNcode: '', OpeningStock: '' });
      }
      setErrors({});
    }
  }, [open, ItemData, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Name.trim()) newErrors.Name = 'Item name is required';

    const numFields = [
      { key: 'PurchaseRate', label: 'Purchase Rate' },
      { key: 'SaleRate', label: 'Sale Rate' },
      { key: 'GSTpercent', label: 'GST %' },
      { key: 'OpeningStock', label: 'Opening Stock' },
    ];
    numFields.forEach(({ key, label }) => {
      if (formData[key] === '' || formData[key] === null || formData[key] === undefined) {
        newErrors[key] = `${label} is required`;
      } else if (isNaN(formData[key]) || parseFloat(formData[key]) < 0) {
        newErrors[key] = `Enter a valid ${label}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicate = () => existingItems.find(i => {
    if (mode === 'edit' && ItemData && i.Id === ItemData.Id) return false;
    return i.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();
  });

  const handleSave = async () => {
    if (!validateForm()) return;
    const duplicate = checkDuplicate();
    if (duplicate) {
      setDuplicateMessage(`An Item with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }
    setLoading(true);
    try {
      const dataToSend = {
        Name: formData.Name,
        PurchaseRate: parseFloat(formData.PurchaseRate) || 0,
        SaleRate: parseFloat(formData.SaleRate) || 0,
        GSTpercent: parseFloat(formData.GSTpercent) || 0,
        HSNcode: formData.HSNcode || '',
        OpeningStock: parseFloat(formData.OpeningStock) || 0,
        CompanyId: companyId,
      };
      if (mode === 'edit') {
        if (!ItemData || !ItemData.Id) { alert('Error: Cannot update Item - missing ID'); return; }
        dataToSend.Id = ItemData.Id;
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        if (ItemData.CreatedBy) dataToSend.CreatedBy = ItemData.CreatedBy;
        if (ItemData.CreatedDate) dataToSend.CreatedDate = ItemData.CreatedDate;
        await updateData('ItemApi', ItemData.Id, dataToSend, token);
      } else {
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        await postData('ItemApi','', dataToSend, token);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving Item:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => ({ create: 'New Item', edit: 'Edit Item', view: 'Item Details' }[mode] || 'Item');
  const isReadOnly = mode === 'view';
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
      <Dialog
        open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
            padding: '2px', margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{ background: 'white', borderRadius: isMobile ? '0' : '10px', overflow: 'hidden', height: isMobile ? '100vh' : 'auto' }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
            color: 'white', padding: '8px 12px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <InventoryIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>{getTitle()}</Typography>
            </Box>
            <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{ padding: '8px 12px', paddingBottom: '4px' }}>
            <Typography variant="subtitle2" style={{ color: '#666', marginBottom: '6px', fontSize: '10px', lineHeight: '1.2' }}>
              {mode === 'create' ? 'Add a new Item to your system' : mode === 'edit' ? 'Update Item information' : 'View Item details'}
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px' }}>
                  Item Information
                </Typography>
              </Grid>

              {/* Name */}
              <Grid item xs={12}>
                <TextField
                  name="Name" label="Item Name *" value={formData.Name} onChange={handleChange}
                  fullWidth size="small" error={!!errors.Name} helperText={errors.Name}
                  disabled={isReadOnly} placeholder="Enter item name" inputProps={{ maxLength: 100 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Purchase Rate & Sale Rate */}
              <Grid item xs={6}>
                <TextField
                  name="PurchaseRate" label="Purchase Rate (₹) *" value={formData.PurchaseRate} onChange={handleChange}
                  fullWidth size="small" type="number" error={!!errors.PurchaseRate} helperText={errors.PurchaseRate}
                  disabled={isReadOnly} placeholder="0.00" inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="SaleRate" label="Sale Rate (₹) *" value={formData.SaleRate} onChange={handleChange}
                  fullWidth size="small" type="number" error={!!errors.SaleRate} helperText={errors.SaleRate}
                  disabled={isReadOnly} placeholder="0.00" inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* GST % & HSN Code */}
              <Grid item xs={6}>
                <TextField
                  name="GSTpercent" label="GST % *" value={formData.GSTpercent} onChange={handleChange}
                  fullWidth size="small" type="number" error={!!errors.GSTpercent} helperText={errors.GSTpercent}
                  disabled={isReadOnly} placeholder="0.00" inputProps={{ min: 0, step: '0.01', max: 100 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="HSNcode" label="HSN Code" value={formData.HSNcode} onChange={handleChange}
                  fullWidth size="small" error={!!errors.HSNcode} helperText={errors.HSNcode}
                  disabled={isReadOnly} placeholder="Enter HSN code" inputProps={{ maxLength: 15 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Opening Stock */}
              <Grid item xs={6}>
                <TextField
                  name="OpeningStock" label="Opening Stock *" value={formData.OpeningStock} onChange={handleChange}
                  fullWidth size="small" type="number" error={!!errors.OpeningStock} helperText={errors.OpeningStock}
                  disabled={isReadOnly} placeholder="0.00" inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions style={{ padding: '8px 12px', background: '#f8f9fa', borderTop: '1px solid #e9ecef', gap: '6px', minHeight: 'auto' }}>
            <Button onClick={onClose} style={{ color: '#6c757d', textTransform: 'none', fontWeight: '500', fontSize: '12px', padding: '4px 8px' }}>
              Cancel
            </Button>
            {!isReadOnly && (
              <Button onClick={handleSave} disabled={loading} variant="contained"
                style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #9a7a2e 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 12px', fontSize: '12px' }}>
                {loading ? 'Saving...' : mode === 'create' ? 'Add Item' : 'Update Item'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '64px', color: '#ff9800', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '12px', color: '#2d3748', fontSize: '16px' }}>
            Duplicate Item Found
          </Typography>
          <Typography style={{ color: '#666', fontSize: '14px', lineHeight: '1.5' }}>{duplicateMessage}</Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button onClick={() => setDuplicateDialogOpen(false)} variant="contained"
            style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #7a5230 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '8px 32px', fontSize: '14px' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ItemModal;
